import React, { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Table, StatusPill, RowActions } from 'shared-portal-component';

import '@/components/organism/Administrators/AdministratorsTable/AdministratorsTable.scss';
import type { AdminRow, Status } from '@/types/administrators.types';

interface AdministratorsTableProps {
  rows: AdminRow[];
  onEdit: (row: AdminRow) => void;
  onToggleActive: (row: AdminRow) => void;
  onForceLogout: (row: AdminRow) => void;
  onForceLoginReset: (row: AdminRow) => void;
  onDelete: (row: AdminRow) => void;
}

const ActionsCell: React.FC<
  { row: AdminRow } & Omit<AdministratorsTableProps, 'rows'>
> = ({ row, onEdit, onToggleActive, onForceLogout, onForceLoginReset, onDelete }) => {
  const actions = useMemo(
    () => [
      { label: 'Edit Admin', onClick: () => { onEdit(row); } },
      {
        label: row.status === 'Active' ? 'Make Inactive' : 'Make Active',
        onClick: () => { onToggleActive(row); },
      },
      { label: 'Force Logout',      onClick: () => { onForceLogout(row); } },
      { label: 'Force Login Reset', onClick: () => { onForceLoginReset(row); } },
      { label: 'Delete Admin',      onClick: () => { onDelete(row); } },
    ],
    [row, onEdit, onToggleActive, onForceLogout, onForceLoginReset, onDelete]
  );

  return <RowActions actions={actions} />;
};

const AdministratorsTable: React.FC<AdministratorsTableProps> = (props) => {
  const { rows } = props;

  const columns: Array<ColumnDef<AdminRow>> = useMemo(
    () => [
      { accessorKey: 'name',  header: 'Administrator' },
      { accessorKey: 'email', header: 'Email' },
      { accessorKey: 'phone', header: 'Phone' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => <StatusPill status={getValue() as Status} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <ActionsCell
            row={row.original}
            onEdit={props.onEdit}
            onToggleActive={props.onToggleActive}
            onForceLogout={props.onForceLogout}
            onForceLoginReset={props.onForceLoginReset}
            onDelete={props.onDelete}
          />
        ),
      },
    ],
    [
      props.onEdit,
      props.onToggleActive,
      props.onForceLogout,
      props.onForceLoginReset,
      props.onDelete,
    ]
  );

  return (
    <Table<AdminRow>
      className="table"
      data={rows}
      columns={columns}
      variant="admin"
      size="sm"
    />
  );
};

export default AdministratorsTable;
